/**
 * The **ARPG class declaration format** (PRD #792), following the Arena
 * class-def idiom in `src/sim/arenaClasses/`.
 *
 * A class used to be a const inside `hero.ts` holding flat vitals, an
 * equipment allowlist, and a loadout. Adding a class meant editing engine code.
 * An {@link ArpgClassDef} is a **pure declaration** instead: no world, no rng,
 * no clock, and no behavior. It names the class, the weapon families it can
 * hold, the equipment it may wear, how its vitals grow with level, its ability
 * kit as catalog ability ids with unlock levels, what it starts with, and the
 * balance floor its guard test asserts.
 *
 * Two rules keep the format honest.
 *
 * **`playable` gates the roster, not the schema.** Every declared class
 * satisfies the same guard whether or not a player can pick it, which is what
 * stops `playable: false` from being a way to ship an empty record. The two
 * stub classes that did exactly that are deleted.
 *
 * **An unpopulated {@link ArpgBalanceFloor} bars a class from `playable`.**
 * That is the mechanism that keeps an untuned class away from players, and it
 * is asserted by the registry guard rather than trusted.
 *
 * This format is designed against one class, which is a known and accepted
 * cost: PRD #800 authors a ranged kit and a casting kit against it and is
 * expected to demand changes. Nothing else consumes it yet, so those changes
 * are cheap.
 */

import type { EquippedItem } from '../../entity';
import type { DamageType, LoadoutBaseEntry } from '../../combat/abilityCatalog';
import type { WeaponGrip } from '../../combat/derivedStats';

/**
 * Every declared class. The Archer and the Mage joined in PRD #800, and the
 * Necromancer in PRD #804.
 */
export type ArpgClassId = 'warrior' | 'archer' | 'mage' | 'necromancer';

/** One weapon family the class can hold, keyed in the def by its equipment id. */
export interface ArpgWeaponFamily {
  damage: number;
  grip: WeaponGrip;
  /**
   * Whether the family is an item worn on the weapon layer. Defaults to true.
   *
   * The Mage is why this exists (PRD #800). A caster's implement is its hands:
   * UO's casting clips are bare-handed animations, there is no baked staff or
   * wand, and the PRD's own rule is that a kit shrinks rather than grows an art
   * budget. So the Mage declares a family that contributes its damage band to
   * the fold and wears nothing, and the "exactly one equipped weapon" rule in
   * `hero.ts` reads this flag rather than assuming every class holds something.
   *
   * It is a schema change the format did not have, and that is the seam finding
   * PRD #800 exists to produce: a format designed against one melee class
   * assumed a held weapon, and the second and third class found the assumption.
   */
  worn?: boolean;
}

/** Whether a weapon family is an item the Hero wears on the weapon layer. */
export function isWornWeaponFamily(family: ArpgWeaponFamily | undefined): boolean {
  return family !== undefined && family.worn !== false;
}

/**
 * A linear curve in level: the value at level 1 plus a step per level after it.
 *
 * Linear is a first-pass choice, not a constraint of the format. It is enough
 * to make deeper content survivable, and its value at level 1 is exactly the
 * flat constant the class shipped with, which is what pins the Warrior
 * regression: progression is additive over the shipped class, not a rebalance
 * of it.
 */
export interface ArpgLevelCurve {
  base: number;
  perLevel: number;
}

/** The class's base contribution to the derivation fold, before any allocation. */
export interface ArpgClassCurves {
  maxHealth: ArpgLevelCurve;
  maxMana: ArpgLevelCurve;
  /** Vitals per tick at any level. Flat until an affix or a status moves them. */
  healthRegenPerTick: number;
  manaRegenPerTick: number;
  /** Base crit, before Dexterity. Zero for a class that starts without one. */
  critChance: number;
  critMultiplier: number;
  /** Innate resists in percentage points, before Wisdom. Absent types are zero. */
  resists?: Partial<Record<DamageType, number>>;
}

/** What a Hero of this class starts wearing and holding. */
export interface ArpgStartingEquipment {
  mainHandId: string;
  equipped: readonly EquippedItem[];
}

/**
 * The floor the class's own guard test asserts, in the Arena idiom of "each
 * class declares its own balance floor" rather than stretching another class's.
 *
 * These are survival and lethality bounds against the one placeholder monster
 * that exists. They are not a balance corpus and they are not a CI gate: the
 * harness and its gates are the tuning PRD's work. What they do is make
 * `playable` mean something a reader can check.
 */
export interface ArpgBalanceFloor {
  /** Ticks the class may take to kill the placeholder monster with its basic. */
  maxTicksToKillPlaceholder: number;
  /** Placeholder swings a level 1 Hero of this class must survive. */
  minPlaceholderSwingsSurvived: number;
  /** Casts of its most expensive ability the class's full mana pool must afford. */
  minCostliestCastsFromFullMana: number;
}

/**
 * Where an ability sits on the action bar, as authored layout rather than as a
 * rule (PRD #800).
 *
 * The bar used to be five hand-written slots naming five Warrior ability ids,
 * which is a per-class client edit for every class that follows. A slot is
 * data now, and it lives on the class rather than on the catalog entry for the
 * same reason `unlockLevel` does: the same ability could sit under a different
 * finger for a different class.
 */
export type ArpgAbilitySlot =
  | 'left-mouse'
  | 'right-mouse'
  | 'key-3'
  | 'key-4'
  | 'key-5';

export const ARPG_ABILITY_SLOTS: readonly ArpgAbilitySlot[] = [
  'left-mouse',
  'right-mouse',
  'key-3',
  'key-4',
  'key-5',
];

/** One kit entry: a catalog ability, the level that unlocks it, and its slot. */
export interface ArpgClassAbility extends LoadoutBaseEntry {
  slot: ArpgAbilitySlot;
}

/** One ARPG class. Pure declaration; every consumer reads it and none edits it. */
export interface ArpgClassDef {
  id: ArpgClassId;
  /** Player-facing name, rendered by the Hero panel. */
  name: string;
  /** One sentence of roster copy, shown before the class has a Hero. */
  blurb: string;
  /** Whether a player may pick this class. Never a licence to ship an empty record. */
  playable: boolean;
  /** Weapon families by equipment id: what the class may hold and what it deals. */
  weapons: Readonly<Record<string, ArpgWeaponFamily>>;
  /** Every equipment id the class may wear or hold. */
  allowedEquipmentIds: ReadonlySet<string>;
  /** The class's base contribution to the derivation fold, by level. */
  curves: ArpgClassCurves;
  /**
   * The ability kit: catalog ability ids, the level that unlocks each one, and
   * the action-bar slot it answers to. Mechanics, clips, and tuning all live on
   * the catalog entry, so the class carries progression and layout and nothing
   * else.
   */
  abilities: readonly ArpgClassAbility[];
  startingEquipment: ArpgStartingEquipment;
  balanceFloor: ArpgBalanceFloor;
}

/** A curve's value at one level. Level is clamped to at least one. */
export function curveAt(curve: ArpgLevelCurve, level: number): number {
  const bounded = Number.isFinite(level) ? Math.max(1, Math.floor(level)) : 1;
  return curve.base + curve.perLevel * (bounded - 1);
}

/** Whether a floor is populated. An unpopulated floor bars a class from `playable`. */
export function hasBalanceFloor(floor: ArpgBalanceFloor | undefined): boolean {
  return (
    floor !== undefined &&
    floor !== null &&
    Number.isFinite(floor.maxTicksToKillPlaceholder) &&
    floor.maxTicksToKillPlaceholder > 0 &&
    Number.isFinite(floor.minPlaceholderSwingsSurvived) &&
    floor.minPlaceholderSwingsSurvived > 0 &&
    Number.isFinite(floor.minCostliestCastsFromFullMana) &&
    floor.minCostliestCastsFromFullMana > 0
  );
}
